import type { RumEvent } from "@datadog/browser-rum";

// Known-benign browser noise discarded before RUM ingestion:
// - "ResizeObserver loop": spec-mandated warning; the observer's remaining
//   notifications are re-delivered next frame, so nothing is lost. Matches
//   both message variants ("...completed with undelivered notifications"
//   and the older "...limit exceeded").
// - "Object Not Found Matching Id": emitted by Microsoft Outlook SafeLinks /
//   Dynamics 365 link scanners that execute pages in an instrumented
//   browser before a user clicks through; never from a real visitor.
// - "signal is aborted without reason": Chrome's message for
//   AbortController.abort() called without an argument, for cancellation
//   errors that were wrapped in a way that loses the AbortError type
//   (isExpectedCancellation catches the unwrapped ones in every browser).
const DISCARDED_ERROR_MESSAGE_SNIPPETS = [
  "ResizeObserver loop",
  "Object Not Found Matching Id",
  "signal is aborted without reason",
];

// Intentional request cancellations are outcomes, not defects: when the user
// supersedes a pending query, navigates away, or a component unmounts,
// TanStack Query aborts the in-flight fetch and connect-web's teardown
// surfaces the abort as AbortError (or ConnectError with code canceled).
// Mirrors the backend treatment of CodeCanceled as a non-error
// (go-api-server/connectutil). Other ConnectError codes — including
// deadline_exceeded, which is an actionable timeout — stay reported.
function isExpectedCancellation(error: { message: string; type?: string }): boolean {
  return (
    error.type === "AbortError" ||
    (error.type === "ConnectError" && error.message.startsWith("[canceled]"))
  );
}

// beforeSend hook for Datadog RUM: returning false discards the event.
export function rumBeforeSend(event: RumEvent): boolean {
  if (event.type !== "error") {
    return true;
  }
  if (DISCARDED_ERROR_MESSAGE_SNIPPETS.some((snippet) => event.error.message.includes(snippet))) {
    return false;
  }
  if (isExpectedCancellation(event.error)) {
    return false;
  }
  return true;
}
